import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "@phosphor-icons/react";
import EmailPreview from "../components/EmailPreview";
import {
  GUIDE_HERO,
  QUICK_CHECK,
  SITUATIONS,
  DO_THIS,
  DONT_DO,
  IF_CLICKED,
  IF_NOT_CLICKED,
  REPORT_CONTACT,
} from "../data/guide";

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.4, delay: i * 0.05 },
});

export default function Guide() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 lg:py-20">
      {/* HERO */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
        <motion.div {...fade(0)} className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">{GUIDE_HERO.eyebrow}</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground mt-3 leading-[1.02]">
            {GUIDE_HERO.title}
          </h1>
          <p className="text-muted-foreground text-lg mt-6 leading-relaxed max-w-xl">{GUIDE_HERO.lede}</p>
        </motion.div>
        <motion.div {...fade(1)} className="md:col-span-5">
          <div className="rounded-2xl overflow-hidden border border-border shadow-sm">
            <img src={GUIDE_HERO.image} alt="Phishing and scams" className="w-full h-[260px] object-cover" />
          </div>
        </motion.div>
      </div>

      {/* QUICK CHECK */}
      <section className="mt-20">
        <motion.div {...fade(0)}>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Recognise it</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-2">
            The 6-point check for every email
          </h2>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Run these six quick checks whenever an email asks you to click, open, pay, or share something.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {QUICK_CHECK.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Warning;
            return (
              <motion.div
                key={c.title}
                {...fade(i)}
                data-testid={`quick-check-${i}`}
                className="rounded-xl bg-white border border-border shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon size={24} weight="duotone" className="text-primary" />
                  </div>
                  <span className="font-heading font-bold text-lg text-foreground">{c.title}</span>
                </div>
                <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{c.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* SITUATIONS WITH EXAMPLES */}
      <section className="mt-20">
        <motion.div {...fade(0)}>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Real examples</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-2">
            Every common scam, with a worked example
          </h2>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Here are the situations you\u2019re most likely to see. Read the example email, then the red flags that give it away.
          </p>
        </motion.div>

        <div className="space-y-8 mt-8">
          {SITUATIONS.map((s, i) => (
            <motion.div
              key={s.id}
              {...fade(0)}
              data-testid={`situation-${i}`}
              className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden"
            >
              <div className="p-6 sm:p-8 border-b border-border">
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-accent/10 text-accent flex items-center justify-center font-heading font-black">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-xl sm:text-2xl text-foreground">{s.title}</h3>
                    <p className="text-muted-foreground mt-1">{s.summary}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 sm:p-8">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-3">
                    Example email
                  </p>
                  <EmailPreview email={s.email} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-accent font-semibold mb-3">
                    Red flags to notice
                  </p>
                  <ul className="space-y-3">
                    {s.flags.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 rounded-lg bg-accent/5 border border-accent/15 p-3">
                        <Icons.WarningCircle size={20} weight="fill" className="text-accent shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* DO / DON'T */}
      <section className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div {...fade(0)} className="rounded-2xl bg-white border border-border shadow-sm p-6 sm:p-8" data-testid="do-list">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#059669]/10 flex items-center justify-center">
              <Icons.CheckCircle size={24} weight="duotone" className="text-[#059669]" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight text-foreground">Do this</h2>
          </div>
          <ul className="mt-5 space-y-3">
            {DO_THIS.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <Icons.Check size={20} weight="bold" className="text-[#059669] shrink-0 mt-0.5" />
                <span className="text-foreground leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div {...fade(1)} className="rounded-2xl bg-white border border-border shadow-sm p-6 sm:p-8" data-testid="dont-list">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-destructive/10 flex items-center justify-center">
              <Icons.XCircle size={24} weight="duotone" className="text-destructive" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight text-foreground">Never do this</h2>
          </div>
          <ul className="mt-5 space-y-3">
            {DONT_DO.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <Icons.X size={20} weight="bold" className="text-destructive shrink-0 mt-0.5" />
                <span className="text-foreground leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* WHAT TO DO NEXT: CLICKED VS NOT */}
      <section className="mt-20">
        <motion.div {...fade(0)}>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">What to do next</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-2">
            Did you click, or not?
          </h2>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Follow the right path below. Either way, reporting quickly is the most important step.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Clicked */}
          <motion.div {...fade(0)} className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden" data-testid="if-clicked">
            <div className="relative h-40">
              <img src={IF_CLICKED.image} alt="If you clicked" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-destructive/70 mix-blend-multiply" />
              <div className="absolute inset-0 flex items-center gap-3 px-6">
                <Icons.WarningOctagon size={30} weight="fill" className="text-white" />
                <h3 className="font-heading font-black text-2xl text-white">If you DID click</h3>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-muted-foreground leading-relaxed">{IF_CLICKED.intro}</p>
              <ol className="mt-5 space-y-4">
                {IF_CLICKED.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-destructive text-white font-heading font-bold text-sm flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-heading font-bold text-foreground">{s.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </motion.div>

          {/* Not clicked */}
          <motion.div {...fade(1)} className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden" data-testid="if-not-clicked">
            <div className="relative h-40">
              <img src={IF_NOT_CLICKED.image} alt="If you did not click" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
              <div className="absolute inset-0 flex items-center gap-3 px-6">
                <Icons.ShieldCheck size={30} weight="fill" className="text-white" />
                <h3 className="font-heading font-black text-2xl text-white">If you did NOT click</h3>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-muted-foreground leading-relaxed">{IF_NOT_CLICKED.intro}</p>
              <ol className="mt-5 space-y-4">
                {IF_NOT_CLICKED.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-white font-heading font-bold text-sm flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-heading font-bold text-foreground">{s.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </motion.div>
        </div>
      </section>

      {/* REPORT / CONTACT */}
      <motion.div
        {...fade(0)}
        data-testid="report-contact"
        className="mt-20 rounded-2xl bg-primary text-white p-8 sm:p-12"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <Icons.Megaphone size={28} weight="duotone" className="text-white" />
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Report anything suspicious</h2>
            </div>
            <p className="text-white/70 mt-3 max-w-xl leading-relaxed">{REPORT_CONTACT.note}</p>
            <div className="flex flex-wrap gap-4 mt-5">
              <a
                href={`tel:${REPORT_CONTACT.phone.replace(/[^0-9]/g, "")}`}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-medium transition-colors duration-200"
              >
                <Icons.Phone size={16} weight="duotone" /> {REPORT_CONTACT.phone}
              </a>
              <a
                href={`mailto:${REPORT_CONTACT.email}`}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-medium transition-colors duration-200"
              >
                <Icons.EnvelopeSimple size={16} weight="duotone" /> {REPORT_CONTACT.email}
              </a>
            </div>
          </div>
          <Link
            to="/test"
            data-testid="guide-cta-test"
            className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-primary font-semibold hover:-translate-y-0.5 transition-transform duration-200"
          >
            Test your knowledge <Icons.ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
